// pages/AddSalesAndTraffic.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Paper, Divider, InputAdornment
} from '@mui/material';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplaceUser } from 'module/documents/utils';
import { useDocumentContext } from 'module/documents/context';
import { postWorkplaceDailySummary, getDayParts, getWorkplaceDailySummary } from 'core/API/logbook';
import {
  fetchLogbookSalesMetricPreferences,
  LogbookSalesMetric
} from 'core/API/logbookSalesMetrics';
import { NO_WORKPLACE_MESSAGE, getCurrentWorkplaceId } from 'core/session/workplaceStore';

interface DayPart {
  '@id': string;
  id: number;
  partName: string;
  startTime?: string;
  endTime?: string;
}

interface MetricWithPreference extends LogbookSalesMetric {
  checked: boolean;
}

interface CategoryGroup {
  category: string;
  dayPartMetrics: MetricWithPreference[];
  dailyMetrics: MetricWithPreference[];
}

// Value storage: { metricId: { dayPartId?: value, 'daily'?: value } }
type MetricValues = Record<number, Record<string, string>>;

const AddSalesAndTraffic: React.FC = () => {
  const queryClient        = useQueryClient();
  const { showToast }      = useToastContext();
  const history            = useHistory();
  const location           = useLocation();
  useWorkplaceUser();
  const { startDate, setStartDate } = useDocumentContext();
  const workplaceId = getCurrentWorkplaceId();

  // Use the date selected in header calendar, fallback to today if not set
  const today = new Date().toISOString().split('T')[0];
  const currentDate = startDate || today;

  // Handle URL date parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get('date');
    if (dateParam) {
      setStartDate(dateParam);
    }
  }, [location.search, setStartDate]);


  const [metricValues, setMetricValues] = useState<MetricValues>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Sync lock — React state alone cannot stop multi-click before re-render (REL-004)
  const isSavingRef = useRef(false);

  // Fetch day parts
  const { data: dayPartsRes } = useQuery(
    ['day-parts'],
    () => getDayParts(),
    {
      onError: (err) => {
        console.error(err);
        showToast('error', 'Failed to load day parts');
      },
      refetchOnWindowFocus: false,
    }
  );

  // Fetch metric preferences to filter active metrics
  const { data: preferencesRes } = useQuery(
    ['logbook-sales-metric-preferences', workplaceId],
    () => fetchLogbookSalesMetricPreferences(workplaceId),
    {
      enabled: !!workplaceId,
      onError: (err) => {
        console.error(err);
        showToast('error', 'Failed to load metric preferences');
      },
      refetchOnWindowFocus: false,
    }
  );

  const dayParts: DayPart[] = useMemo(() => {
    return dayPartsRes?.member || dayPartsRes?.['hydra:member'] || [];
  }, [dayPartsRes]);

  // Extract active metrics directly from preferences (they contain expanded metric objects)
  const activeMetrics: MetricWithPreference[] = useMemo(() => {
    if (!preferencesRes) return [];
    
    const prefs = preferencesRes; // Already an array from fetchLogbookSalesMetricPreferences
    
    
    const metrics = prefs
      .filter((pref: any) => pref.active === true) // Only active preferences
      .map((pref: any) => {
        const metric = pref.metric;
        // Handle both expanded object and IRI string
        if (typeof metric === 'object' && metric.id) {
          // Add "by day part" suffix for DAY_PART metrics if not already present
          let displayName = metric.name || '';
          if (metric.metricType === 'DAY_PART' && !displayName.toLowerCase().includes('by day part')) {
            displayName = `${displayName} by day part`;
          }
          
          return {
            ...metric,
            name: displayName,
            checked: true,
          };
        }
        return null;
      })
      .filter((m): m is MetricWithPreference => m !== null);
    
    return metrics;
  }, [preferencesRes]);

  // Group metrics by category and separate DAY_PART vs DAILY
  const categoryGroups: CategoryGroup[] = useMemo(() => {
    const categoryMap: Record<string, CategoryGroup> = {};

    activeMetrics.forEach((metric) => {
      const category = String(metric.category || 'Uncategorized');
      if (!categoryMap[category]) {
        categoryMap[category] = {
          category,
          dayPartMetrics: [],
          dailyMetrics: [],
        };
      }

      if (metric.metricType === 'DAY_PART') {
        categoryMap[category].dayPartMetrics.push(metric);
      } else {
        categoryMap[category].dailyMetrics.push(metric);
      }
    });

    const groups = Object.values(categoryMap);
    return groups;
  }, [activeMetrics, dayParts]);

  // Input validation functions
  const validateIntegerInput = (value: string): string => {
    // Allow negative numbers but no decimals
    // Remove all non-digit and non-minus characters
    let cleaned = value.replace(/[^0-9-]/g, '');
    // Ensure minus sign only appears at the start
    if (cleaned.includes('-')) {
      const hasLeadingMinus = cleaned.startsWith('-');
      cleaned = cleaned.replace(/-/g, '');
      if (hasLeadingMinus) {
        cleaned = '-' + cleaned;
      }
    }
    return cleaned;
  };

  // Format number with commas
  const formatNumberWithCommas = (value: string | number): string => {
    if (!value && value !== 0) return '';
    const num = typeof value === 'string' ? value.replace(/,/g, '') : String(value);
    if (num === '' || num === '-') return num;
    const isNegative = num.startsWith('-');
    const absoluteNum = isNegative ? num.slice(1) : num;
    if (absoluteNum === '') return '-';
    const formatted = absoluteNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return isNegative ? `-${formatted}` : formatted;
  };

  // Parse number removing commas
  const parseNumber = (value: string): string => {
    return value.replace(/,/g, '');
  };

  // Convert to title case
  const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  const handleMetricInputChange = (metricId: number, dayPartIdOrDaily: string, value: string) => {
    const parsedValue = parseNumber(value);
    const validatedValue = validateIntegerInput(parsedValue);
    setMetricValues(prev => ({
      ...prev,
      [metricId]: {
        ...(prev[metricId] || {}),
        [dayPartIdOrDaily]: validatedValue,
      },
    }));
  };

  // Fetch existing record for the day to prefill
  const dailySummaryQuery = useQuery(
    ['workplaceDailySummary', workplaceId, currentDate],
    () => getWorkplaceDailySummary({ workplaceId, date: currentDate }),
    { enabled: !!workplaceId && !!currentDate, refetchOnWindowFocus: false }
  );

  // Prefill metricValues from existing record
  useEffect(() => {
    const first = dailySummaryQuery.data?.member?.[0];
    if (!first || activeMetrics.length === 0 || dayParts.length === 0) return;


    const newMetricValues: MetricValues = {};

    // Helper to find metric by name (case-insensitive)
    const findMetric = (searchName: string) => {
      return activeMetrics.find(m => 
        m.name?.toLowerCase() === searchName.toLowerCase()
      );
    };

    // Helper to find day part ID by name (case-insensitive)
    const findDayPartId = (searchName: string) => {
      const dayPart = dayParts.find(dp => 
        dp.partName.toLowerCase() === searchName.toLowerCase()
      );
      return dayPart ? String(dayPart.id) : null;
    };

    // 1. Prefill sales object -> Sales metric
    if (first.sales && typeof first.sales === 'object') {
      const salesMetric = findMetric('sales') || findMetric('sales by day part');
      if (salesMetric?.id) {
        newMetricValues[salesMetric.id] = {};
        Object.entries(first.sales).forEach(([dayPartName, value]) => {
          const dayPartId = findDayPartId(dayPartName);
          if (dayPartId) {
            newMetricValues[salesMetric.id][dayPartId] = String(value);
          }
        });
      }
    }

    // 2. Prefill traffic object -> Customer count metric
    if (first.traffic && typeof first.traffic === 'object') {
      const customerMetric = activeMetrics.find(m => 
        m.name?.toLowerCase().includes('customer count')
      );
      if (customerMetric?.id) {
        newMetricValues[customerMetric.id] = {};
        Object.entries(first.traffic).forEach(([dayPartName, value]) => {
          const dayPartId = findDayPartId(dayPartName);
          if (dayPartId) {
            newMetricValues[customerMetric.id][dayPartId] = String(value);
          }
        });
      }
    }

    // 3. Prefill salesMetricsDaily -> Daily metrics
    if (first.salesMetricsDaily && typeof first.salesMetricsDaily === 'object') {
      Object.entries(first.salesMetricsDaily).forEach(([metricName, value]) => {
        const metric = findMetric(metricName);
        if (metric?.id && metric.metricType !== 'DAY_PART') {
          if (!newMetricValues[metric.id]) {
            newMetricValues[metric.id] = {};
          }
          newMetricValues[metric.id]['daily'] = String(value);
        }
      });
    }

    // 4. Prefill salesMetricsDayPart -> Other day part metrics
    if (first.salesMetricsDayPart && typeof first.salesMetricsDayPart === 'object') {
      Object.entries(first.salesMetricsDayPart).forEach(([metricName, dayPartValues]) => {
        const metric = findMetric(metricName);
        if (metric?.id && metric.metricType === 'DAY_PART' && typeof dayPartValues === 'object') {
          if (!newMetricValues[metric.id]) {
            newMetricValues[metric.id] = {};
          }
          Object.entries(dayPartValues).forEach(([dayPartName, value]) => {
            const dayPartId = findDayPartId(dayPartName);
            if (dayPartId) {
              newMetricValues[metric.id][dayPartId] = String(value);
            }
          });
        }
      });
    }

    setMetricValues(newMetricValues);
  }, [dailySummaryQuery.data?.member, activeMetrics, dayParts]);

  // Calculate totals (if needed for display)
  const totalValues = useMemo(() => {
    const totals: Record<number, number> = {};
    Object.entries(metricValues).forEach(([metricId, dayPartValues]) => {
      const sum = Object.values(dayPartValues).reduce((acc, val) => acc + (Number(val) || 0), 0);
      totals[Number(metricId)] = Math.round(sum * 100) / 100;
    });
    return totals;
  }, [metricValues]);

  // Returns list of empty required field labels
  const emptyFields = useMemo(() => {
    const empty: string[] = [];
    categoryGroups.forEach((group) => {
      group.dayPartMetrics.forEach((metric) => {
        const metricId = metric.id ?? 0;
        dayParts.forEach((dayPart) => {
          const val = metricValues[metricId]?.[String(dayPart.id)];
          if (val === undefined || val === '' || val === '-') {
            empty.push(toTitleCase(`${dayPart.partName} ${metric.name}`));
          }
        });
      });
      group.dailyMetrics.forEach((metric) => {
        const metricId = metric.id ?? 0;
        const val = metricValues[metricId]?.['daily'];
        if (val === undefined || val === '' || val === '-') {
          empty.push(toTitleCase(metric.name || ''));
        }
      });
    });
    return empty;
  }, [metricValues, categoryGroups, dayParts]);

  const handleSave = async () => {
    if (isSavingRef.current) return;
    if (!workplaceId) {
      showToast('error', NO_WORKPLACE_MESSAGE);
      return;
    }
    setHasSubmitted(true);
    if (emptyFields.length > 0) {
      showToast('error', 'All fields are required. Please enter 0 for any field with no value.');
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      // Build the payload structure for the API
      const payload: any = {
        workplace: `api/workplaces/${workplaceId}`,
        author: `api/users/4`,
        date: currentDate,
      };

      const sales: Record<string, number> = {};
      const traffic: Record<string, number> = {};
      const salesMetricsDaily: Record<string, number> = {};
      const salesMetricsDayPart: Record<string, Record<string, number>> = {};

      // Process each metric
      Object.entries(metricValues).forEach(([metricId, dayPartValues]) => {
        const metric = activeMetrics.find(m => m.id === Number(metricId));
        if (!metric) return;

        const metricName = metric.name || '';
        const isCustomerCount = metricName.toLowerCase().includes('customer count');
        const isSales = metricName.toLowerCase() === 'sales' || 
                        metricName.toLowerCase() === 'sales by day part';

        if (metric.metricType === 'DAY_PART') {
          // Process day part metrics
          Object.entries(dayPartValues).forEach(([dayPartId, value]) => {
            if (value === '' || value === '-') return;

            const dayPart = dayParts.find(dp => dp.id === Number(dayPartId));
            if (!dayPart) return;

            const dayPartKey = dayPart.partName.toLowerCase();
            const numValue = Number(value);

            if (isSales) {
              // Sales metric goes into sales object
              sales[dayPartKey] = numValue;
            } else if (isCustomerCount) {
              // Customer count goes into traffic object
              traffic[dayPartKey] = numValue;
            } else {
              // Other day part metrics go into salesMetricsDayPart
              if (!salesMetricsDayPart[metricName]) {
                salesMetricsDayPart[metricName] = {};
              }
              salesMetricsDayPart[metricName][dayPartKey] = numValue;
            }
          });
        } else {
          // Process daily metrics
          const dailyValue = dayPartValues['daily'];
          if (dailyValue !== undefined && dailyValue !== '' && dailyValue !== '-' && !isSales && !isCustomerCount) {
            salesMetricsDaily[metricName] = Number(dailyValue);
          }
        }
      });

      // Add objects to payload only if they have data
      if (Object.keys(sales).length > 0) {
        payload.sales = sales;
      }
      if (Object.keys(traffic).length > 0) {
        payload.traffic = traffic;
      }
      if (Object.keys(salesMetricsDaily).length > 0) {
        payload.salesMetricsDaily = salesMetricsDaily;
      }
      if (Object.keys(salesMetricsDayPart).length > 0) {
        payload.salesMetricsDayPart = salesMetricsDayPart;
      }


      // Use unified API to post metric data
      await postWorkplaceDailySummary(payload);

      await queryClient.invalidateQueries(['workplaceDailySummary', workplaceId, currentDate]);

      showToast('success', 'Saved successfully!');
      // Redirect to logbook while preserving the selected date
      history.push(`${ROUTES.LOGBOOK}?date=${currentDate}`);
    } catch (e) {
      console.error(e);
      showToast('error', 'Save failed.');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: '20px', bgcolor: 'whitesmoke', minHeight: '100vh' }}>
      {/* Header: Back + Save */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          variant="contained"
          onClick={() => history.goBack()}
          sx={{
            bgcolor: 'rgb(68,99,136)',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            px: 4,
            '&:hover': { bgcolor: 'rgb(57,80,111)' },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            bgcolor: 'rgb(68,99,136)',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            px: 4,
            '&:hover': { bgcolor: 'rgb(57,80,111)' },
          }}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      {/* Metadata row */}
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          bgcolor: '#fff',
          px: 2,
          py: 1.5,
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <Typography variant="body2">
          <strong>Date:</strong>{' '}
          {new Date(currentDate + 'T00:00:00').toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })}
        </Typography>
        <Typography variant="body2">
          <strong>Workplace ID:</strong> {workplaceId}
        </Typography>
      </Box>

      {/* Render each category as a separate section card */}
      {categoryGroups.map((group, groupIdx) => (
        <Paper
          variant="outlined"
          key={groupIdx}
          sx={{ mb: 2, bgcolor: '#fff', borderColor: '#e0e0e0' }}
        >
          {/* Section title */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {toTitleCase(group.category)}
            </Typography>
          </Box>

          {/* Card body */}
          <Box sx={{ p: 3 }}>
            {/* DAY_PART metrics section */}
            {group.dayPartMetrics.length > 0 && dayParts.length > 0 && (
              <>
                {/* Headers - one for each metric */}
                <Grid container spacing={2} mb={2}>
                  {group.dayPartMetrics.map((metric) => (
                    <Grid item xs={12} md={12 / group.dayPartMetrics.length} key={metric.id}>
                      <Typography variant="subtitle1">
                        {toTitleCase(metric.name || '')}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                {/* Row-by-row layout for each day part */}
                <Grid container spacing={2}>
                  {dayParts.map((dayPart, dayPartIndex) => (
                    <React.Fragment key={dayPart.id}>
                      {group.dayPartMetrics.map((metric, metricIndex) => {
                        const metricId = metric.id ?? 0;
                        const dayPartId = String(dayPart.id);
                        const rawValue = metricValues[metricId]?.[dayPartId] || '';
                        const displayValue = formatNumberWithCommas(rawValue);
                        const isNegative = rawValue && Number(rawValue) < 0;
                        const tabIndex = dayPartIndex * group.dayPartMetrics.length + metricIndex + 1;
                        const isCurrency = group.category !== 'Operational' &&
                          !metric.name.toLowerCase().includes('customer count');

                        const isFieldEmpty = hasSubmitted && (rawValue === '' || rawValue === undefined || rawValue === '-');
                        return (
                          <Grid item xs={12} md={12 / group.dayPartMetrics.length} key={`${dayPart.id}-${metric.id}`}>
                            <TextField
                              label={toTitleCase(`${dayPart.partName} ${metric.name}`)}
                              value={displayValue}
                              onChange={(e) =>
                                handleMetricInputChange(metricId, dayPartId, e.target.value)
                              }
                              onFocus={(e) => e.target.select()}
                              fullWidth
                              type="text"
                              tabIndex={tabIndex}
                              InputLabelProps={{ shrink: true }}
                              InputProps={isCurrency ? {
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              } : undefined}
                              inputProps={{
                                pattern: '[0-9,-]*',
                                inputMode: 'numeric',
                                style: isNegative ? { color: 'red' } : {}
                              }}
                              error={isFieldEmpty}
                              helperText={isFieldEmpty ? 'Required — enter 0 if no value' : undefined}
                            />
                          </Grid>
                        );
                      })}
                    </React.Fragment>
                  ))}

                  {/* Total rows */}
                  {group.dayPartMetrics.map((metric) => {
                    const metricId = metric.id ?? 0;
                    const total = totalValues[metricId] || 0;
                    const formattedTotal = formatNumberWithCommas(total);
                    const isNegative = total < 0;
                    const isCurrency = group.category !== 'Operational' &&
                      !metric.name.toLowerCase().includes('customer count');
                    return (
                      <Grid item xs={12} md={12 / group.dayPartMetrics.length} key={`total-${metric.id}`}>
                        <TextField
                          label={toTitleCase(`Total ${metric.name}`)}
                          value={formattedTotal}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                            ...(isCurrency && {
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }),
                          }}
                          inputProps={{
                            style: isNegative ? { color: 'red' } : {}
                          }}
                          variant="filled"
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}

            {/* Divider between day part and daily metrics */}
            {group.dayPartMetrics.length > 0 && group.dailyMetrics.length > 0 && (
              <Divider sx={{ my: 3 }} />
            )}

            {/* DAILY metrics section */}
            {group.dailyMetrics.length > 0 && (
              <Grid container spacing={2}>
                {group.dailyMetrics.map((metric) => {
                  const metricId = metric.id ?? 0;
                  const rawValue = metricValues[metricId]?.['daily'] || '';
                  const displayValue = formatNumberWithCommas(rawValue);
                  const isNegative = rawValue && Number(rawValue) < 0;
                  const isCurrency = group.category !== 'Operational' &&
                    !metric.name.toLowerCase().includes('customer count');
                  const isDailyFieldEmpty = hasSubmitted && (rawValue === '' || rawValue === undefined || rawValue === '-');
                  return (
                    <Grid item xs={12} sm={6} md={4} key={metric.id}>
                      <TextField
                        label={metric.name || ''}
                        value={displayValue}
                        onChange={(e) =>
                          handleMetricInputChange(metricId, 'daily', e.target.value)
                        }
                        onFocus={(e) => e.target.select()}
                        fullWidth
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        InputProps={isCurrency ? {
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        } : undefined}
                        inputProps={{
                          pattern: '[0-9,-]*',
                          inputMode: 'numeric',
                          style: isNegative ? { color: 'red' } : {}
                        }}
                        error={isDailyFieldEmpty}
                        helperText={isDailyFieldEmpty ? 'Required — enter 0 if no value' : undefined}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Paper>
      ))}

      {/* No metrics message */}
      {categoryGroups.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, bgcolor: '#fff' }}>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            No active metrics configured. Please enable metrics in Logbook Settings.
          </Typography>
        </Paper>
      )}
    </Paper>
  );
};

export default AddSalesAndTraffic;
